import React, { useEffect, useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { formsAPI } from '../../utils/crmApi';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../../contexts/BrandingContext';
import { EmptyStates } from '../ui/empty-state';
import { useDialog } from '../kibo-ui/dialog';
import toast from 'react-hot-toast';

const FormsHome = () => {
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState([]);
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { openDialog } = useDialog();

  const loadForms = async () => {
    try {
      setLoading(true);
      const { data } = await formsAPI.getAll();
      setForms(data.data || []);
    } catch (e) {
      console.error('Failed to load forms', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadForms(); }, []);

  const handleCreate = async () => {
    try {
      const { data } = await formsAPI.create({ name: 'Untitled form' });
      const id = data.data?._id;
      if (id) navigate(`/forms/${id}/builder`);
    } catch (e) {
      console.error('Failed to create form', e);
      toast.error('Failed to create form');
    }
  };

  const handleDelete = (formId, formName) => {
    openDialog({
      title: 'Delete Form',
      description: `Are you sure you want to delete "${formName}"? This action cannot be undone and will remove all associated submissions.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await formsAPI.delete(formId);
          toast.success('Form deleted successfully');
          loadForms();
        } catch (e) {
          console.error('Failed to delete form', e);
          toast.error('Failed to delete form');
        }
      }
    });
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-foreground">Forms</h1>
        <button 
          onClick={handleCreate} 
          className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2"
          style={{ 
            backgroundColor: branding?.primaryColor || '#3b82f6',
            '--tw-ring-color': branding?.primaryColor || '#3b82f6'
          }}
        >
          <PlusIcon className="w-5 h-5" />
          Create form
        </button>
      </div>
      {loading ? (
        <div className="text-muted-foreground">Loading...</div>
      ) : (
        <div className="overflow-x-auto bg-card rounded-md shadow border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Updated</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-gray-200">
              {forms.map(f => (
                <tr 
                  key={f._id} 
                  className="hover:bg-muted cursor-pointer"
                  onClick={() => navigate(`/forms/${f._id}/builder`)}
                >
                  <td className="px-4 py-3 text-sm text-foreground">{f.name}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${f.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-muted text-foreground'}`}>{f.status}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{new Date(f.updatedAt).toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/forms/${f._id}/builder`)}
                        className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded"
                        title="Edit form"
                      >
                        Edit
                      </button>
                      {f.status === 'published' && (
                        <>
                          <button
                            onClick={() => navigate(`/forms/${f._id}/analytics`)}
                            className="px-2 py-1 text-xs rounded hover:opacity-80 transition-opacity"
                            style={{ 
                              color: branding?.primaryColor || '#3b82f6',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = `${branding?.primaryColor || '#3b82f6'}15`;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            title="View analytics"
                          >
                            Analytics
                          </button>
                          <button
                            onClick={() => navigate(`/forms/${f._id}/responses`)}
                            className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded"
                            title="View responses"
                          >
                            Responses
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(f._id, f.name)}
                        className="px-2 py-1 text-xs text-red-600 hover:text-red-900 hover:bg-red-50 rounded flex items-center gap-1"
                        title="Delete form"
                      >
                        <TrashIcon className="h-3 w-3" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {forms.length === 0 && (
                <tr>
                  <td className="px-4 py-12" colSpan={4}>
                    <EmptyStates.NoForms 
                      onCreateForm={handleCreate}
                      branding={branding}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FormsHome;


